import React, { useRef } from 'react';
import { FormControl, IconButton, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDispatch } from 'react-redux';
import { SortOption } from '../Sell';
import { useInventoryFilters } from './hooks/useInventoryFilters';
import { toggleFilterCollapsed } from '../../../../store/slices/sidebarFilterSlice';
import {InventoryFilterState} from '../../../skin-db/database/filter/SkinDbFilter';

interface InventoryHeaderProps {
  filters: InventoryFilterState;
  sortBy: SortOption;
  searchTerm: string;
  currencySymbol: string; // Ensure this is string
  loading: boolean;
  manualCooldown: number;
  isExtensionInstalled: boolean;
  onSearchChange: (val: string) => void;
  onSortChange: (val: SortOption) => void;
  onUpdateFilters: (f: InventoryFilterState) => void;
  onResetFilters: () => void;
  onRefresh: () => void;
}

const InventoryHeader: React.FC<InventoryHeaderProps> = ({
                                                           filters,
                                                           sortBy,
                                                           searchTerm,
                                                           currencySymbol,
                                                           loading,
                                                           manualCooldown,
                                                           isExtensionInstalled,
                                                           onSearchChange,
                                                           onSortChange,
                                                           onUpdateFilters,
                                                           onResetFilters,
                                                           onRefresh,
                                                         }) => {
  const dispatch = useDispatch();

  // Convert symbol safely to string just in case the prop type is broader than string
  const safeCurrencySymbol = String(currencySymbol);

  const { activeChips, removeFilter } = useInventoryFilters(filters, onUpdateFilters, safeCurrencySymbol);

  const chipContainerRef = useRef<HTMLDivElement | null>(null);
  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!chipContainerRef.current) return;
    isDown.current = true;
    startX.current = e.pageX - chipContainerRef.current.offsetLeft;
    scrollLeft.current = chipContainerRef.current.scrollLeft;
  };

  const handleMouseLeaveOrUp = () => {
    isDown.current = false;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDown.current || !chipContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - chipContainerRef.current.offsetLeft;
    const walk = (x - startX.current) * 2;
    chipContainerRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const formatCooldown = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const renderChips = (isMobile: boolean) => {
    if (activeChips.length === 0) return null;
    return (
        <div
            className={`${isMobile ? 'lg:hidden' : 'hidden lg:flex'} flex items-center gap-2 overflow-x-auto px-1 scrollbar-none select-none cursor-grab active:cursor-grabbing`}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeaveOrUp}
            onMouseUp={handleMouseLeaveOrUp}
            onMouseMove={handleMouseMove}
            ref={isMobile ? null : chipContainerRef}
        >
          {activeChips.length > 1 && (
              <button
                  onClick={onResetFilters}
                  className="flex items-center justify-center bg-red-500/20 text-red-500 p-2 rounded-lg shrink-0 border border-red-500/30 active:scale-95 transition-transform"
              >
                <DeleteSweepIcon fontSize="small" />
              </button>
          )}
          {activeChips.map((chip, idx) => (
              <div
                  key={`${chip.key}-${idx}`}
                  className="flex items-center bg-neutral-800 border border-neutral-700 rounded-lg overflow-hidden shrink-0"
              >
                <button
                    onClick={() => removeFilter(chip.key, chip.value)}
                    className="px-2 py-1.5 bg-neutral-700/50 hover:bg-red-600 group transition-colors border-r border-neutral-700"
                >
                  <CloseIcon sx={{ fontSize: 14 }} className="text-neutral-400 group-hover:text-white" />
                </button>
                <span className="px-3 text-[11px] text-neutral-300 font-bold whitespace-nowrap uppercase">
              {chip.label}
            </span>
              </div>
          ))}
        </div>
    );
  };

  return (
      <div className="flex flex-col gap-2 mb-2.5">
        {renderChips(true)}
        <div className="flex justify-between items-center py-3 px-4 bg-neutral-900/80 backdrop-blur-md shadow-lg shadow-dark-primary/30 md:rounded-xl rounded-lg border-none relative min-h-[60px]">
          <div className="flex gap-3 items-center flex-shrink-0">
            <IconButton
                onClick={() => dispatch(toggleFilterCollapsed())}
                className="bg-neutral-800 hover:bg-neutral-700 transition-colors"
                sx={{ borderRadius: '8px', border: 'none', padding: '8px' }}
            >
              <FilterListIcon className="text-dark-primary text-xl" />
            </IconButton>
            <TextField
                className="md:w-64 w-36"
                size="small"
                placeholder="Search Inventory"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#262626',
                    '& fieldset': { border: 'none' },
                    '& input': { color: '#e5e5e5', fontSize: '14px' },
                  },
                }}
            />
          </div>
          <div className="hidden lg:flex flex-1 mx-4 overflow-hidden items-center">{renderChips(false)}</div>
          <div className="flex items-center gap-3">
            <FormControl size="small" className="md:w-52 w-40 flex-shrink-0">
              <Select
                  value={sortBy}
                  onChange={(e) => onSortChange(e.target.value as SortOption)}
                  variant="outlined"
                  MenuProps={{ disableScrollLock: true }}
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: '#262626',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiSelect-icon': { color: '#737373' },
                  }}
              >
                <MenuItem value="price_desc">Price: Highest</MenuItem>
                <MenuItem value="price_asc">Price: Lowest</MenuItem>
                <MenuItem value="title_asc">Title A → Z</MenuItem>
                <MenuItem value="title_desc">Title Z → A</MenuItem>
                <MenuItem value="float_asc">Float: Lowest</MenuItem>
                <MenuItem value="float_desc">Float: Highest</MenuItem>
              </Select>
            </FormControl>
            <div className="hidden md:flex items-center bg-blue-50/40 dark:bg-white/[0.03] border border-blue-100 dark:border-white/5 px-4 rounded-xl h-10 flex-shrink-0">
              <Tooltip title={isExtensionInstalled ? 'Extension: 2m Cooldown' : 'No Extension: 10m Cooldown'} arrow>
                <div className="flex flex-col items-start min-w-[55px]">
                <span className="text-[8px] text-blue-500 dark:text-gray-500 font-black uppercase tracking-widest leading-none">
                  Fetch
                </span>
                  <span className="text-[12px] font-mono font-black text-blue-600 dark:text-gray-200 leading-tight">
                  {manualCooldown > 0 ? formatCooldown(manualCooldown) : 'READY'}
                </span>
                </div>
              </Tooltip>
              <div className="flex items-center gap-2">
                <div className="h-6 w-[1px] bg-blue-200 dark:bg-white/10 mx-1" />
                <Tooltip title={manualCooldown > 0 ? `Wait (${manualCooldown}s)` : 'Refresh Inventory Data'} arrow>
                <span>
                  <IconButton
                      onClick={onRefresh}
                      disabled={manualCooldown > 0 || loading}
                      size="small"
                      className={`!p-1.5 ${
                          manualCooldown > 0
                              ? 'opacity-30'
                              : 'text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-white/5'
                      }`}
                  >
                    <RefreshIcon
                        sx={{ fontSize: 18 }}
                        className={`${
                            manualCooldown === 0 && !loading ? 'hover:rotate-180 transition-transform duration-500' : ''
                        } ${loading ? 'animate-spin' : ''}`}
                    />
                  </IconButton>
                </span>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default InventoryHeader;